/**
 * Admin Add-on Bundle Management Page (Phase 4: Add-on bundles)
 *
 * Bundle builder + CRUD. A bundle is a fixed-price set of add-ons (each with a
 * quantity) sharing one discount. Quote-based add-ons can't be bundled, so the
 * picker excludes them. A live price preview mirrors the backend math.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Package,
  Eye,
  EyeOff,
  X,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import { useToast } from '../../../../components/common/ToastProvider';
import { ApiError } from '../../../../utils/apiClient';
import { getCoinRate, DEFAULT_COIN_RATE } from '../../../../services/settingsService';
import adminService, {
  type Addon,
  type AddonBundle,
  type CreateBundleParams,
  type AddonAudience,
  type DiscountKind,
} from '../../../../services/adminService';

const AUDIENCES: AddonAudience[] = ['OWNER', 'BROKER', 'COMPANY', 'ALL'];
const DISCOUNT_KINDS: DiscountKind[] = ['NONE', 'PERCENTAGE', 'FIXED_SAR', 'COINS'];

/** Apply a discount to a base SAR amount (client mirror of the backend math). */
function computeDiscountSar(
  kind: DiscountKind,
  value: number,
  baseSar: number,
  coinRate: number,
): number {
  let discount = 0;
  if (kind === 'PERCENTAGE') discount = Math.floor((baseSar * (value || 0)) / 100);
  else if (kind === 'FIXED_SAR') discount = value || 0;
  else if (kind === 'COINS') discount = (value || 0) * coinRate;
  return Math.max(0, Math.min(discount, baseSar));
}

// ============================================================================
// BUNDLE FORM MODAL
// ============================================================================

interface BundleItemState {
  addonId: string;
  quantity: string;
}

interface BundleFormState {
  slug: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  audiences: AddonAudience[];
  discountKind: DiscountKind;
  discountValue: string;
  isActive: boolean;
  sortOrder: string;
  items: BundleItemState[];
}

function BundleFormModal({
  bundle,
  addons,
  coinRate,
  onSave,
  onClose,
  isLoading,
}: {
  bundle?: AddonBundle;
  addons: Addon[];
  coinRate: number;
  onSave: (data: CreateBundleParams) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { t, i18n } = useTranslation('bundles');
  const isRTL = i18n.language === 'ar';
  const isEdit = !!bundle;

  const localizedAddon = useCallback(
    (a: Pick<Addon, 'name' | 'nameAr'>) => (isRTL ? a.nameAr || a.name : a.name),
    [isRTL],
  );

  const [formData, setFormData] = useState<BundleFormState>({
    slug: bundle?.slug ?? '',
    name: bundle?.name ?? '',
    nameAr: bundle?.nameAr ?? '',
    description: bundle?.description ?? '',
    descriptionAr: bundle?.descriptionAr ?? '',
    audiences: bundle?.audiences ?? ['ALL'],
    discountKind: bundle?.discountKind ?? 'NONE',
    discountValue:
      bundle?.discountValue !== undefined && bundle?.discountValue !== null
        ? String(bundle.discountValue)
        : '0',
    isActive: bundle?.isActive ?? true,
    sortOrder: bundle?.sortOrder !== undefined ? String(bundle.sortOrder) : '0',
    items:
      bundle?.items?.map((it) => ({
        addonId: it.addonId,
        quantity: String(it.quantity || 1),
      })) ?? [],
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Add-on lookup by id (only bundleable add-ons are passed in).
  const addonById = useMemo(() => {
    const map = new Map<string, Addon>();
    addons.forEach((a) => map.set(a.id, a));
    return map;
  }, [addons]);

  const toggleAudience = (audience: AddonAudience) => {
    setFormData((prev) => {
      const has = prev.audiences.includes(audience);
      return {
        ...prev,
        audiences: has
          ? prev.audiences.filter((a) => a !== audience)
          : [...prev.audiences, audience],
      };
    });
  };

  const addItem = () => {
    // Default to the first add-on not already in the bundle.
    const used = new Set(formData.items.map((i) => i.addonId));
    const next = addons.find((a) => !used.has(a.id));
    if (!next) return;
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { addonId: next.id, quantity: '1' }],
    }));
  };

  const updateItem = (index: number, patch: Partial<BundleItemState>) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Live price preview.
  const baseSar = useMemo(
    () =>
      formData.items.reduce((sum, it) => {
        const addon = addonById.get(it.addonId);
        const unit = addon?.priceSar ?? 0;
        const qty = Number(it.quantity) || 0;
        return sum + unit * qty;
      }, 0),
    [formData.items, addonById],
  );
  const discountSar = useMemo(
    () =>
      computeDiscountSar(formData.discountKind, Number(formData.discountValue) || 0, baseSar, coinRate),
    [formData.discountKind, formData.discountValue, baseSar, coinRate],
  );
  const finalSar = baseSar - discountSar;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (formData.audiences.length === 0) {
      setFormError(t('admin.audiencesRequired'));
      return;
    }
    if (formData.items.length === 0) {
      setFormError(t('admin.itemsRequired'));
      return;
    }

    const submitData: CreateBundleParams = {
      slug: formData.slug.trim(),
      name: formData.name.trim(),
      nameAr: formData.nameAr.trim() || undefined,
      description: formData.description.trim() || undefined,
      descriptionAr: formData.descriptionAr.trim() || undefined,
      audiences: formData.audiences,
      discountKind: formData.discountKind,
      discountValue:
        formData.discountKind === 'NONE' ? 0 : Number(formData.discountValue) || 0,
      isActive: formData.isActive,
      sortOrder: formData.sortOrder.trim() ? Number(formData.sortOrder) : 0,
      items: formData.items.map((it) => ({
        addonId: it.addonId,
        quantity: Number(it.quantity) || 1,
      })),
    };

    onSave(submitData);
  };

  const inputClass =
    'w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  const discountValueHint =
    formData.discountKind === 'PERCENTAGE'
      ? t('admin.fieldDiscountValueHintPercentage')
      : formData.discountKind === 'FIXED_SAR'
        ? t('admin.fieldDiscountValueHintFixed')
        : formData.discountKind === 'COINS'
          ? t('admin.fieldDiscountValueHintCoins')
          : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white">
            {isEdit ? t('admin.editBundle') : t('admin.createBundle')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Slug */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldSlug')}</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className={`${inputClass} font-mono`}
              placeholder="e.g., starter_pack"
              required
              disabled={isEdit}
            />
            {!isEdit && <p className="text-xs text-gray-500 mt-1">{t('admin.fieldSlugHint')}</p>}
          </div>

          {/* Name EN + AR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.fieldNameAr')}</label>
              <input
                type="text"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className={inputClass}
                dir="rtl"
              />
            </div>
          </div>

          {/* Description EN + AR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldDescription')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`${inputClass} resize-none`}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('admin.fieldDescriptionAr')}
              </label>
              <textarea
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                className={`${inputClass} resize-none`}
                rows={2}
                dir="rtl"
              />
            </div>
          </div>

          {/* Audiences */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldAudiences')}</label>
            <div className="flex flex-wrap gap-3">
              {AUDIENCES.map((aud) => (
                <label
                  key={aud}
                  className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border dark:border-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={formData.audiences.includes(aud)}
                    onChange={() => toggleAudience(aud)}
                    className="w-4 h-4 rounded text-[#004D40]"
                  />
                  <span className="text-sm">{t(`audience.${aud}`)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Add-ons in this bundle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">{t('admin.fieldItems')}</label>
              <Button
                size="sm"
                variant="outline"
                onClick={addItem}
                disabled={addons.length === 0 || formData.items.length >= addons.length}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('admin.addItem')}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">{t('admin.fieldItemsHint')}</p>

            {addons.length === 0 ? (
              <p className="text-sm text-amber-600">{t('admin.noAddonsAvailable')}</p>
            ) : formData.items.length === 0 ? (
              <p className="text-sm text-gray-500">{t('admin.itemsRequired')}</p>
            ) : (
              <div className="space-y-2">
                {formData.items.map((item, index) => {
                  // Disallow picking the same add-on twice.
                  const usedElsewhere = new Set(
                    formData.items.filter((_, i) => i !== index).map((i) => i.addonId),
                  );
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg border dark:border-gray-600"
                    >
                      <select
                        value={item.addonId}
                        onChange={(e) => updateItem(index, { addonId: e.target.value })}
                        className={`${inputClass} flex-1`}
                      >
                        {addons.map((a) => (
                          <option
                            key={a.id}
                            value={a.id}
                            disabled={usedElsewhere.has(a.id)}
                          >
                            {localizedAddon(a)}
                            {a.priceSar != null
                              ? `${t('priceSar', { price: a.priceSar.toLocaleString() })}`
                              : ''}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{t('admin.quantity')}</span>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, { quantity: e.target.value })}
                          className="w-16 px-2 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          min="1"
                          max="100"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title={t('admin.remove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Discount picker (segmented) + value */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.fieldDiscount')}</label>
            <div className="flex flex-wrap gap-2">
              {DISCOUNT_KINDS.map((kind) => {
                const active = formData.discountKind === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setFormData({ ...formData, discountKind: kind })}
                    className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                      active
                        ? 'bg-[#004D40] border-[#004D40] text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(`discountKind.${kind}`)}
                  </button>
                );
              })}
            </div>
            {formData.discountKind !== 'NONE' && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">
                  {t('admin.fieldDiscountValue')}
                </label>
                <input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  className={inputClass}
                  min="0"
                  max={formData.discountKind === 'PERCENTAGE' ? '100' : undefined}
                />
                {discountValueHint && (
                  <p className="text-xs text-gray-500 mt-1">{discountValueHint}</p>
                )}
              </div>
            )}
          </div>

          {/* Price preview */}
          <div className="rounded-xl bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10 border border-[var(--pv-color-accent-soft)] p-4">
            <p className="text-sm font-semibold text-[#003930] dark:text-[#B7DDD7] mb-2">
              {t('admin.previewTitle')}
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">{t('admin.previewBase')}</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {t('priceSar', { price: baseSar.toLocaleString() })}
                </span>
              </div>
              {discountSar > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t('admin.previewDiscount')}
                  </span>
                  <span className="font-medium text-green-600">
                    −{t('priceSar', { price: discountSar.toLocaleString() })}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-1 border-t border-[var(--pv-color-accent-soft)]/60">
                <span className="text-gray-700 dark:text-gray-200 font-semibold">
                  {t('admin.previewFinal')}
                </span>
                <span className="font-bold text-[#004D40]">
                  {t('priceSar', { price: finalSar.toLocaleString() })}
                </span>
              </div>
            </div>
          </div>

          {/* Sort order + active */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <label className="text-sm">{t('admin.fieldSortOrder')}:</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                className="w-20 px-2 py-1 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded text-[#004D40]"
              />
              <span className="text-sm">{t('admin.fieldActive')}</span>
            </label>
          </div>

          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {formError}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('admin.saving')}
                </>
              ) : (
                t('admin.save')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminBundlesPage() {
  const { t, i18n } = useTranslation('bundles');
  const isRTL = i18n.language === 'ar';
  const { showToast } = useToast();

  const localizedAddon = useCallback(
    (a: Pick<Addon, 'name' | 'nameAr'>) => (isRTL ? a.nameAr || a.name : a.name),
    [isRTL],
  );

  const [bundles, setBundles] = useState<AddonBundle[]>([]);
  // Bundleable add-ons (priced, non-quote) used to populate the item picker.
  const [addons, setAddons] = useState<Addon[]>([]);
  const [coinRate, setCoinRate] = useState<number>(DEFAULT_COIN_RATE);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingBundle, setEditingBundle] = useState<AddonBundle | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [bundleList, addonList, rate] = await Promise.all([
        adminService.getBundles(),
        adminService.getAddons(),
        getCoinRate(),
      ]);
      setBundles(bundleList);
      // Exclude quote-based add-ons — the backend rejects them in bundles.
      setAddons(addonList.filter((a) => !a.isQuoteBased && a.priceSar != null));
      setCoinRate(rate);
    } catch (err) {
      console.error('Failed to load bundles:', err);
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Map a backend error to a friendly, localized message.
  const errorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError && err.details && typeof err.details === 'object') {
      const code = (err.details as { code?: string }).code;
      if (code === 'SLUG_EXISTS') return t('admin.slugExistsError');
      if (code === 'QUOTE_ADDON_IN_BUNDLE') return t('admin.quoteAddonError');
    }
    return fallback;
  };

  const handleCreate = async (data: CreateBundleParams) => {
    try {
      setIsSaving(true);
      await adminService.createBundle(data);
      setShowCreateModal(false);
      showToast(t('admin.createBundle'), 'success');
      await loadData();
    } catch (err) {
      console.error('Failed to create bundle:', err);
      showToast(errorMessage(err, t('admin.createError')), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: CreateBundleParams) => {
    if (!editingBundle) return;
    try {
      setIsSaving(true);
      // Slug cannot be changed.
      const { slug: _slug, ...updateData } = data;
      void _slug;
      await adminService.updateBundle(editingBundle.id, updateData);
      setEditingBundle(null);
      showToast(t('admin.editBundle'), 'success');
      await loadData();
    } catch (err) {
      console.error('Failed to update bundle:', err);
      showToast(errorMessage(err, t('admin.updateError')), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (bundle: AddonBundle) => {
    if (!confirm(t('admin.deleteConfirm', { name: bundle.name }))) return;
    try {
      await adminService.deleteBundle(bundle.id);
      showToast(t('admin.save'), 'success');
      await loadData();
    } catch (err) {
      console.error('Failed to delete bundle:', err);
      showToast(t('admin.deleteError'), 'error');
    }
  };

  // Base SAR price of a bundle from its items (for the table column).
  const baseSarOf = (bundle: AddonBundle): number =>
    bundle.items.reduce(
      (sum, it) => sum + (it.addon?.priceSar ?? 0) * (it.quantity || 1),
      0,
    );

  const discountLabel = (bundle: AddonBundle): string => {
    if (bundle.discountKind === 'NONE' || !bundle.discountValue) {
      return t('discountKind.NONE');
    }
    if (bundle.discountKind === 'PERCENTAGE') return `${bundle.discountValue}%`;
    if (bundle.discountKind === 'FIXED_SAR') {
      return t('priceSar', { price: bundle.discountValue.toLocaleString() });
    }
    return t('coinPrice', { count: bundle.discountValue });
  };

  const finalSarOf = (bundle: AddonBundle): number => {
    const base = baseSarOf(bundle);
    const discount = computeDiscountSar(
      bundle.discountKind,
      bundle.discountValue || 0,
      base,
      coinRate,
    );
    return base - discount;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                {t('admin.title')}
              </h1>
              <p className="text-gray-500">{t('admin.subtitle')}</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.addBundle')}
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colName')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colItems')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colDiscount')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colAudiences')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colPrice')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colStatus')}
                  </th>
                  <th className="px-4 py-3 text-end text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {bundles.map((bundle) => (
                  <tr key={bundle.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#004D40]" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {bundle.name}
                          </p>
                          {bundle.nameAr && (
                            <p className="text-xs text-gray-500" dir="rtl">
                              {bundle.nameAr}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="block">
                        {t('admin.itemsCount', { count: bundle.items.length })}
                      </span>
                      <span className="text-xs text-gray-500 line-clamp-1">
                        {bundle.items
                          .map((it) =>
                            it.addon
                              ? t('itemLine', {
                                  name: localizedAddon(it.addon),
                                  count: it.quantity,
                                })
                              : '',
                          )
                          .filter(Boolean)
                          .join('، ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {discountLabel(bundle)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {bundle.audiences.map((aud) => (
                          <span
                            key={aud}
                            className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          >
                            {t(`audience.${aud}`)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {t('priceSar', { price: finalSarOf(bundle).toLocaleString() })}
                    </td>
                    <td className="px-4 py-3">
                      {bundle.isActive ? (
                        <span className="text-green-600 flex items-center gap-1 text-sm">
                          <Eye className="w-4 h-4" /> {t('admin.colActive')}
                        </span>
                      ) : (
                        <span className="text-gray-400 flex items-center gap-1 text-sm">
                          <EyeOff className="w-4 h-4" /> {t('admin.colInactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end whitespace-nowrap">
                      <button
                        onClick={() => setEditingBundle(bundle)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                        title={t('admin.editBundle')}
                      >
                        <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(bundle)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title={t('cancel')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {bundles.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {t('admin.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <BundleFormModal
          addons={addons}
          coinRate={coinRate}
          onSave={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isLoading={isSaving}
        />
      )}

      {/* Edit Modal */}
      {editingBundle && (
        <BundleFormModal
          bundle={editingBundle}
          addons={addons}
          coinRate={coinRate}
          onSave={handleUpdate}
          onClose={() => setEditingBundle(null)}
          isLoading={isSaving}
        />
      )}
    </div>
  );
}
